import { Link, useLocation } from "react-router-dom";
import { Search, Music2, Instagram, Youtube, Twitch, Facebook, BookOpen } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { useLocalePath } from "@/components/LocaleRouter";
import { LanguageCurrencySelector } from "@/components/LanguageCurrencySelector";

interface NavPlatform {
  label: string;
  path: string;
  icon: JSX.Element;
}

const PLATFORMS: NavPlatform[] = [
  {
    label: "TikTok",
    path: "/tiktok",
    icon: (
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.5 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.4a6.33 6.33 0 00-.79-.05A6.34 6.34 0 003.15 15.7a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.05a8.27 8.27 0 004.76 1.5V7.12a4.83 4.83 0 01-1-.43z" />
      </svg>
    ),
  },
  { label: "YouTube", path: "/youtube", icon: <Youtube className="h-3.5 w-3.5" /> },
  { label: "Spotify", path: "/spotify", icon: <Music2 className="h-3.5 w-3.5" /> },
  { label: "Instagram", path: "/instagram", icon: <Instagram className="h-3.5 w-3.5" /> },
  { label: "Twitch", path: "/twitch", icon: <Twitch className="h-3.5 w-3.5" /> },
  {
    label: "X",
    path: "/twitter",
    icon: (
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    ),
  },
  { label: "Facebook", path: "/facebook", icon: <Facebook className="h-3.5 w-3.5" /> },
];

export const Navbar = () => {
  const { t } = useI18n();
  const localePath = useLocalePath();
  const { pathname } = useLocation();

  // Strip locale prefix (e.g. /es/youtube → /youtube) for active matching
  const cleanPath = pathname.replace(/^\/[a-z]{2}(?=\/|$)/, "") || "/";
  const isActive = (path: string) =>
    path === "/" ? cleanPath === "/" : cleanPath.startsWith(path);

  return (
    <nav className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-14 sm:h-16 max-w-7xl items-center justify-between gap-3 px-3 sm:px-6 lg:px-8">
        <Link to={localePath("/")} className="flex shrink-0 items-center gap-2">
          <div
            className="flex h-8 w-8 items-center justify-center rounded-lg"
            style={{ background: "var(--hero-gradient)", boxShadow: "0 0 12px hsl(185 100% 50% / 0.3)" }}
          >
            <svg className="h-4.5 w-4.5 text-primary-foreground" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.5 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.4a6.33 6.33 0 00-.79-.05A6.34 6.34 0 003.15 15.7a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V9.05a8.27 8.27 0 004.76 1.5V7.12a4.83 4.83 0 01-1-.43z" />
            </svg>
          </div>
          <span
            className="text-xl sm:text-2xl font-bold text-primary neon-text"
            style={{ fontFamily: "var(--font-display)" }}
          >
            TokDadddy
          </span>
        </Link>

        {/* Platform links — icon-only on small screens, icon+label on xl+ */}
        <div className="min-w-0 flex-1 overflow-x-auto scrollbar-none">
          <div className="flex items-center justify-start gap-0.5 px-1 md:justify-center md:gap-1">
            {PLATFORMS.map((p) => {
              const active = isActive(p.path);
              return (
                <Link
                  key={p.path}
                  to={localePath(p.path)}
                  title={p.label}
                  aria-label={p.label}
                  className={[
                    "flex shrink-0 items-center gap-1.5 rounded-lg p-2 text-xs font-semibold transition-all xl:px-2.5 xl:py-1.5 xl:text-sm",
                    active
                      ? "bg-primary/10 text-primary shadow-[0_0_12px_hsl(185_100%_50%/0.2)]"
                      : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                  ].join(" ")}
                >
                  {p.icon}
                  <span className="hidden xl:inline">{p.label}</span>
                </Link>
              );
            })}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <LanguageCurrencySelector />
          <Link
            to={localePath("/blog")}
            title="Blog"
            aria-label="Blog"
            className="hidden sm:flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 py-2 text-sm font-medium text-muted-foreground transition-all hover:border-primary/40 hover:text-primary hover:shadow-[0_0_12px_hsl(185_100%_50%/0.2)]"
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden md:inline">Blog</span>
          </Link>
          <Link
            to={localePath("/dashboard")}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-secondary px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-muted-foreground transition-all hover:border-primary/40 hover:text-primary hover:shadow-[0_0_12px_hsl(185_100%_50%/0.2)]"
          >
            <Search className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
            <span className="hidden xs:inline">{t.nav.trackOrder}</span>
            <span className="xs:hidden">{t.nav.track}</span>
          </Link>
        </div>
      </div>
    </nav>
  );
};
